import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Box,
} from "@mui/material";
import { useEffect, useMemo, useState } from "react";
import { useBudget } from "../../../../contexts/BudgetContext/projectbudgets.context";
import { useProject } from "../../../../contexts/project.context";
import {
  emptyTotalSplited,
  initialBudgetWideAppliedTaxes,
} from "../../../../types/budget/budget";
import BaseValuesTotal from "../BaseValuesTotal";
import Exchange from "../Exchange";
import BaseValues from "./BaseValues";
import { TaxData } from "../../../../types/budget/service-item";
import { getVisibleStageGrandTotal } from "../../../../utils/visible-stage-totals";

export default function GlobalAppliedTaxes() {
  const {
    activeBudget,
    versionInfo,
    activeBudgetDispatch,
    updateBudget,
    showNotification,
  } = useBudget();
  const { event } = useProject();
  const [isDirty, setIsDirty] = useState(false);
  const baseValuesTotal = useMemo(() => {
    if (versionInfo.version === 0) {
      return (
        activeBudget.budgetValuesTotal?.byCalculationType?.SUM?.SUM ||
        emptyTotalSplited
      );
    } else if (versionInfo.status === "APPROVED") {
      return (
        activeBudget.closingValuesTotal?.byCalculationType?.SUM?.SUM ||
        emptyTotalSplited
      );
    } else {
      return (
        activeBudget.negociationValuesTotal[versionInfo.version - 1]
          ?.byCalculationType?.SUM?.SUM || emptyTotalSplited
      );
    }
  }, [activeBudget, versionInfo]);

  /** Total da versão aberta: agregado gravado, ou células visíveis quando hidratado. */
  const versionTotalInBRL = useMemo(
    () =>
      getVisibleStageGrandTotal(
        activeBudget,
        versionInfo.status,
        versionInfo.version
      ),
    [activeBudget, versionInfo]
  );

  // Reparo em memória de agregados legados (MMV-2511).
  //
  // Antes da correção, `calculateDetailedTotalsNegotiation` não agregava
  // `byCalculationType.SUM.SUM` — orçamentos salvos naquele período têm esse
  // campo zerado no banco, e como nenhuma ação de carregamento dispara
  // recálculo, o card de Totais abriria zerado até alguém editar algo.
  //
  // A condição abaixo só age no estado comprovadamente inconsistente:
  // agregado zerado E a versão tendo valor. Orçamento realmente vazio não entra.
  // Nada é gravado: o recálculo vive na memória e o banco se acerta no
  // próximo save real do usuário.
  useEffect(() => {
    if (!activeBudget?._id) return;
    if (baseValuesTotal.totals.totalInBRL !== 0) return;
    if (versionTotalInBRL === 0) return;

    activeBudgetDispatch({
      type: "RECALCULATE_TOTALS",
      payload: {},
      versionInfo,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    activeBudget?._id,
    versionInfo.version,
    versionInfo.status,
    baseValuesTotal,
    versionTotalInBRL,
  ]);

  const budgetWideAppliedTaxes = useMemo(() => {
    const found = activeBudget.budgetWideAppliedTaxes?.find(
      (el) => el.version === versionInfo.version
    );
    // Always stamp the open version. Falling back to initialBudgetWideAppliedTaxes
    // alone leaves version: 0, so edits on Negociação/Fechamento are saved under
    // v0 while the UI still looks up the open version → flash then reset to 0.
    return (
      found ?? {
        ...initialBudgetWideAppliedTaxes,
        version: versionInfo.version,
      }
    );
  }, [activeBudget, versionInfo]);

  const budgetWideAppliedTaxesComparison = useMemo(() => {
    const found = activeBudget.budgetWideAppliedTaxes?.find(
      (el) => el.version === versionInfo.comparisonVersion
    );
    return (
      found ?? {
        ...initialBudgetWideAppliedTaxes,
        version: versionInfo.comparisonVersion,
      }
    );
  }, [activeBudget, versionInfo]);
  // const [localAppliedTaxes, setLocalAppliedTaxes] = useState(budgetWideAppliedTaxes)
  function handleFieldBlur(value: TaxData) {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    if (
      JSON.stringify(budgetWideAppliedTaxes?.taxData) === JSON.stringify(value)
    ) {
      return;
    }
    // setLocalAppliedTaxes({ ...localAppliedTaxes, taxData })
    activeBudgetDispatch({
      type: "NEW_GLOBAL_TAX_DATA",
      // versionInfo precisa ser irmão de type/payload: é assim que o reducer lê
      // (action.versionInfo). Aninhado em payload ele chega undefined e o recálculo
      // cai no fallback da versão 0, gravando no Orçamento em vez da versão aberta.
      payload: {
        newBudgetWideAppliedTaxes: {
          ...budgetWideAppliedTaxes,
          taxData: value,
          version: versionInfo.version,
        },
      },
      versionInfo,
    });
    setIsDirty(true);
  }
  function handleExchangeSubmit(value: any) {
    // Verifica se o valor realmente mudou antes de disparar o dispatch
    if (
      JSON.stringify(budgetWideAppliedTaxes?.exchange) === JSON.stringify(value)
    ) {
      return;
    }
    // setLocalAppliedTaxes({ ...localAppliedTaxes, exchange: value })
    activeBudgetDispatch({
      type: "NEW_GLOBAL_TAX_DATA",
      payload: {
        newBudgetWideAppliedTaxes: {
          ...budgetWideAppliedTaxes,
          exchange: value,
          version: versionInfo.version,
        },
      },
      versionInfo,
    });
    setIsDirty(true);
  }
  async function handleSave(e: any) {
    if (!isDirty) {
      showNotification("Não há alterações para salvar.", "info");
      return;
    }
    const result = await updateBudget(
      activeBudget?._id || "",
      activeBudget,
      "SAVE",
      versionInfo.version
    );
    // updateBudget retorna null em caso de erro (catch interno); em caso de sucesso
    // pode retornar o budget atualizado ou "" (resposta vazia da API) — por isso o
    // check é por !== null, não por truthy.
    if (result !== null) {
      setIsDirty(false);
      showNotification("Informações salvas com sucesso.", "success");
    }
    // Em caso de erro, updateBudget já dispara showNotification(..., "warning") internamente
    // e mantém os dados não persistidos na tela para nova tentativa.
  }

  return (
    <Accordion>
      <AccordionSummary>
        <span>Valores</span>
      </AccordionSummary>
      <AccordionDetails>
        <BaseValues
          budgetWideAppliedTaxes={budgetWideAppliedTaxes}
          taxData={budgetWideAppliedTaxes?.taxData}
          exchange={budgetWideAppliedTaxes?.exchange}
          compareTaxData={budgetWideAppliedTaxesComparison?.taxData}
          compareExchange={budgetWideAppliedTaxesComparison?.exchange}
          onFieldBlur={handleFieldBlur}
        />
        <Box display={"flex"} justifyContent={"flex-end"}>
          <Button onClick={handleSave} variant="outlined" disabled={!isDirty}>
            Salvar
          </Button>
        </Box>
        <Exchange
          onSubmitExchange={handleExchangeSubmit}
          compareExchangeData={budgetWideAppliedTaxesComparison?.exchange}
          savedExchangeData={budgetWideAppliedTaxes?.exchange}
          eventScope={event?.eventScope}
        />

        <BaseValuesTotal
          totals={baseValuesTotal}
          showExtras={true}
          displayCurrencyCode={budgetWideAppliedTaxes?.exchange?.currency}
        />
      </AccordionDetails>
    </Accordion>
  );
}
